import React, { useEffect, useMemo, useRef, useState } from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import api from "../../utils/axios";
import { ColDef } from "ag-grid-enterprise";
import FilterSection from "../../components/FilterOrderSection";
import { useNavigate } from "react-router";
import PaginationTable from "../../components/Pagination";
import "./index.css";
import { currencySymbol, valueDateFormatter } from "../../utils/formatters";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { toast } from "react-toastify";
import useLang from "../../hooks/useLang";
import { useMediaQuery } from "@mui/material";
import { fetchAllPages, usePagedList } from "../../hooks/usePagedList";

const buyerValueGetter = (params: any) => {
	const text =
		params.data.buyerNameAndSurname +
		" (" +
		params.data.buyerPhoneNo +
		")" +
		"\n" +
		params.data.city +
		", " +
		params.data.country;
	return text;
};

const codeFormatter = (params: any) => {
	const date = valueDateFormatter(params.data.createdAt);
	const text =
		"#" +
		params.data.displayId +
		" (" +
		params.data.statusDescription +
		") " +
		"\n " +
		date;
	return text;
};

const WaitingPickupOrders = () => {
	const { strings } = useLang("waitingPickupOrders");

	let defaultExcelExportSettings = {};

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef<AgGridReact | null>(null);

	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	// The API filters and pages; null = the filters can match nothing.
	const [filters, setFilters] = useState<Record<string, unknown> | null>({});
	const navigate = useNavigate();

	const calculateRowHeight = (params: any) => {
		const cellContent =
			params.data.buyerNameAndSurname +
			"\n" +
			"(" +
			params.data.buyerPhoneNo +
			")" +
			"\n" +
			params.data.createdAt;
		const lineHeight = 30;
		const numLines = cellContent.split("\n").length;
		const calculatedHeight = lineHeight * numLines;

		return calculatedHeight;
	};
	const orderDecriptionValueGetter = (params: any) => {
		// Symbol comes from the order's own registration currency, not the deployment's.
		const symbol = currencySymbol(params.data.orderCurrencyId);
		const text =
			strings?.buyerDetails +
			params.data.orderPrice +
			symbol +
			"\n" +
			strings?.transport +
			params.data.transportPrice +
			symbol;
		return text;
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Columns only: a language switch relabels them without reloading the orders above.
	useEffect(() => {
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 170,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.kodiHeaderName,
				field: "id",
				valueGetter: codeFormatter,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
					"white-space": "pre",
				}),
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyerHeaderName,
				valueGetter: buyerValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescriptionHeaderName,
				valueGetter: orderDecriptionValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [
		setTableColumns,
		strings?.buyerHeaderName,
		strings?.kodiHeaderName,
		strings?.orderDescriptionHeaderName,
	]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		if (colId === "id") {
			await onOrderIdClicked(params?.data);
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const filterOrders = (
		countryIds: any[],
		cityIds: any[],
		statusIds: any[],
		id: any,
		orderDescription: any,
		fromDate: any,
		toDate: any,
		filterType: any
	) => {
		const datesChosen = fromDate && toDate;
		const byRegistration =
			datesChosen && filterType === "registration_date";
		const byPickup =
			datesChosen &&
			(filterType === "acceptance_date" || filterType === "closure_date");
		setCurrentPage(1);
		// These orders were never picked up, and a range that ends before it starts holds nothing:
		// no rows, as before, without asking the API.
		if (byPickup || (byRegistration && fromDate.isAfter(toDate, "day"))) {
			setFilters(null);
			return;
		}
		setFilters({
			countryIds: countryIds?.length ? countryIds : undefined,
			cityIds: cityIds?.length ? cityIds : undefined,
			// Both boxes search the same fields; the code box wins when both are filled. The API
			// takes up to 100 characters.
			search:
				(
					String(id ?? "")
						.trim()
						.replace(/^#/, "") ||
					String(orderDescription ?? "").trim()
				).slice(0, 100) || undefined,
			fromDate: byRegistration
				? fromDate.format("YYYY-MM-DD")
				: undefined,
			toDate: byRegistration ? toDate.format("YYYY-MM-DD") : undefined,
		});
	};

	const customOptions = [
		{
			label: strings?.customOptions.printTicket,
			value: "print_ticket",
		},
		{
			label: strings?.customOptions.printList,
			value: "print_list",
		},
		{
			label: strings?.customOptions.exportExcel,
			value: "export_excel",
		},
		{
			label: strings?.customOptions.delete,
			value: "delete",
		},
	];

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const orderParams = useMemo(
		() => (filters ? { ...filters, currentPage, pageSize } : null),
		[filters, currentPage, pageSize]
	);
	const orders = usePagedList<any>(
		"Order/waiting-pickup-orders",
		orderParams,
		setCurrentPage
	);

	const onPageChanged = (page: any) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const selectedColumnIds = [
		"displayId",
		"buyerNameAndSurname",
		"orderDescription",
	];

	const onExportSelectedId = async () => {
		// Ticked rows export as they are; otherwise every row matching the filters, all pages.
		const ticked = grid.current?.api?.getSelectedRows()?.length;
		let rows: any[] = [];
		try {
			if (!ticked && filters) {
				rows = await fetchAllPages<any>(
					"Order/waiting-pickup-orders",
					filters
				);
			}
		} catch (error) {
			console.log(error);
			return;
		}
		const dataToExport = rows.map((item: any) => {
			const date = valueDateFormatter(item.createdAt);
			const displayId =
				"#" +
				item.displayId +
				" (" +
				item.statusDescription +
				") " +
				"\n " +
				date;
			return {
				displayId,
				buyerNameAndSurname:
					item.buyerNameAndSurname +
					" (" +
					item.buyerPhoneNo +
					")" +
					"\n" +
					item.city +
					", " +
					item.country,
				orderDescription:
					strings?.buyerDetails +
					item.orderPrice +
					currencySymbol(item.orderCurrencyId) +
					"\n" +
					strings?.transport +
					item.transportPrice +
					currencySymbol(item.orderCurrencyId),
			};
		});
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const printTickets = async (format: string) => {
		const ids = (grid.current as any).api
			.getSelectedRows()
			?.map((item: any) => ({
				id: item.displayId,
			}));

		if (!ids?.length) {
			toast.warning(strings?.printTicketsWarning);
			return true;
		}
		const formatA4 = format === "A4";
		try {
			const response = await api.post("Order/print-order-ticket", ids, {
				params: { formatA4 },
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(strings?.popupBlocked);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const printOrderList = async () => {
		const ids = grid?.current?.api.getSelectedRows()?.map((item: any) => {
			return { id: item.id };
		});

		if (!ids || ids.length === 0) {
			toast.warning(strings?.noOrdersSelected);
			return;
		}

		try {
			const response = await api.post("Order/order-list-details", ids, {
				responseType: "blob",
			});

			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(strings?.popupError);
			}
		} catch (error) {
			console.error("Error exporting order list details:", error);
		}
	};

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<FilterSection
						title={strings?.filterSectionTitle}
						onFilterChange={filterOrders}
						selectedOptions={customOptions}
						onExportSelectedId={onExportSelectedId}
						showAdminFilters={false}
						showStatusFilter={false}
						descriptionDebounceMs={400}
						onPrintTickets={printTickets}
						onPrintList={printOrderList}
					/>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={orders.totalCount}
						startOrderIndex={orders.firstRow}
						endOrderIndex={orders.lastRow}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={orders.items}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							getRowHeight={calculateRowHeight}
							onCellClicked={onCellClicked}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default WaitingPickupOrders;
